/**
 * PATH: packages/email-hub/src/feature/components/MarkupDrawer.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-MARKUPDRAWER-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/MarkupDrawer.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/MarkupDrawer.tsx:
 *     thin re-export shim that points back here.
 */

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Copy, Pen, RotateCw, Send, X } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { promptDialog } from '@quotech/ui'
import {
  blobToDataUrl, fetchImageBlob, writeImageToClipboard,
} from '@quotech/email-hub/feature/lib/imageClipboard'
import { fetchImageViaProxy } from '@quotech/email-hub/feature/lib/imageProxy'
import {
  DEFAULT_FONT_SIZE, DEFAULT_STROKE_WIDTH, MARKUP_COLORS,
  exportCanvasToPng, fitCanvasToImage, pointerToCanvas, renderMarkup,
  type MarkupTool, type Point, type Stroke,
} from '@quotech/email-hub/feature/lib/markupCanvas'
import {
  findTopStrokeAt, getStrokeLabel, setStrokeLabel, strokeBounds,
  translateStroke, type Bounds,
} from '@quotech/email-hub/feature/lib/markupGeometry'
import {
  composeUrlFor, useComposePendingStore, type ComposeIntent,
} from '../stores/composePendingStore'
import { MarkupToolbar } from './MarkupToolbar'

export interface MarkupDrawerProps {
  /** ``null`` means closed. The string is an image URL we will
   *  fetch into the background canvas. */
  src: string | null
  /** If the operator opened the drawer from the lightbox of a
   *  specific email, "Send marked-up reply" is offered. */
  anchorEmailId: string | null
  onClose: () => void
}

export function MarkupDrawer({ src, anchorEmailId, onClose }: MarkupDrawerProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const setPending = useComposePendingStore((s) => s.setPending)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const bgImageRef = useRef<HTMLImageElement | null>(null)

  const [tool, setTool] = useState<MarkupTool>('arrow')
  const [color, setColor] = useState<string>(MARKUP_COLORS[0])
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [draftStroke, setDraftStroke] = useState<Stroke | null>(null)
  // Index of the stroke the operator picked with the Select tool (drag to
  // move, double-click to edit its label). null = nothing selected.
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  // Active drag session for the Select tool: which stroke + the last
  // canvas point, so each move applies an incremental delta.
  const dragRef = useRef<{ index: number; last: Point } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // BUG-0030 (a): explicit error state. Previously a failed fetch /
  // decode silently set isLoading=false leaving the canvas at its
  // default 300×150 size with no image — operator saw a blank
  // rectangle with no clue what failed. Now we surface the failure
  // shape so the operator can act on it (retry, copy URL, ask ops
  // to check CORS, etc.).
  const [loadError, setLoadError] = useState<MarkupLoadError | null>(null)
  // Bump this to retrigger the effect after a "Retry" click.
  const [retryNonce, setRetryNonce] = useState(0)

  const closeAndReset = useCallback(() => {
    setStrokes([]); setDraftStroke(null); bgImageRef.current = null
    setLoadError(null); setIsLoading(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!src) return
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    setStrokes([])
    setDraftStroke(null)
    ;(async () => {
      // Strategy A — direct fetch. Handles inline (cid:) attachments
      // served from our origin and the rare public host that emits a
      // wildcard CORS header. Cheap (no backend round-trip) so it goes
      // first.
      let result = await fetchImageBlob(src)
      if (cancelled) return
      // Strategy B — same-origin backend proxy. The common failure is
      // a REMOTE body image (marketing CDN etc.) on a host that sends
      // no CORS headers for the dashboard origin — the browser refuses
      // to hand those bytes to fetch()/canvas. The proxy fetches them
      // server-side and re-emits same-origin, so this succeeds where
      // the direct fetch can't (BUG-2026-05-30-EMAIL-MARKUP-REMOTE-CORS).
      if (!result) {
        result = await fetchImageViaProxy(src)
        if (cancelled) return
      }
      if (!result) {
        // Both strategies exhausted. Either the remote host is dead /
        // 4xx, or the proxy itself rejected the url (SSRF guard, not an
        // image), or the inline signed-url token expired.
        // eslint-disable-next-line no-console
        console.error('[MarkupDrawer] fetchImageBlob + proxy failed', { src })
        setLoadError({ kind: 'fetch', src })
        setIsLoading(false)
        return
      }
      const url = URL.createObjectURL(result)
      const img = new Image()
      img.onload = () => {
        if (cancelled) { URL.revokeObjectURL(url); return }
        const canvas = canvasRef.current
        if (canvas) {
          const { width, height } = fitCanvasToImage(img)
          canvas.width = width; canvas.height = height
        }
        bgImageRef.current = img
        repaint([])
        setIsLoading(false)
        URL.revokeObjectURL(url)
      }
      img.onerror = () => {
        // We got the bytes but the browser refused to decode them.
        // Most common cause is a content-type/extension mismatch
        // (e.g. backend serves a PDF as image/png), HEIC on a
        // browser without the codec, or an SVG that violates the
        // browser's strict SVG sandbox.
        // eslint-disable-next-line no-console
        console.error('[MarkupDrawer] <img> decode failed', { src, mime: result.type })
        setLoadError({ kind: 'decode', src, mime: result.type })
        setIsLoading(false)
        URL.revokeObjectURL(url)
      }
      img.src = url
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, retryNonce])

  // Clean render of just the marks (no selection chrome) — what gets
  // exported to PNG. Also used to paint the background the instant the
  // image decodes.
  const repaint = useCallback((all: ReadonlyArray<Stroke>) => {
    const canvas = canvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d'); if (!ctx) return
    renderMarkup(ctx, bgImageRef.current, all)
  }, [])

  // Real-canvas text measurer for hit-testing / selection bounds, so a
  // click on a label lands on its true painted width (not an estimate).
  const measureText = useCallback((text: string, fontSize: number): number => {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return text.length * fontSize * 0.6
    ctx.save()
    ctx.font = `bold ${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`
    const w = ctx.measureText(text).width
    ctx.restore()
    return w
  }, [])

  // On-screen paint = marks + the dashed selection outline. The outline
  // is screen-only; `handleCopy` / `handleSend` repaint clean first so it
  // never bakes into the exported image.
  const paintLive = useCallback(() => {
    repaint(draftStroke ? [...strokes, draftStroke] : strokes)
    if (tool === 'select' && selectedIndex != null && strokes[selectedIndex]) {
      const ctx = canvasRef.current?.getContext('2d')
      if (ctx) drawSelectionBox(ctx, strokeBounds(strokes[selectedIndex], measureText))
    }
  }, [repaint, strokes, draftStroke, tool, selectedIndex, measureText])

  useEffect(() => { paintLive() }, [paintLive])

  // Selecting only makes sense while the Select tool is active — drop the
  // highlight when the operator switches to a drawing tool.
  useEffect(() => {
    if (tool !== 'select') setSelectedIndex(null)
  }, [tool])

  useEffect(() => {
    if (!src) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndReset()
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); setStrokes((s) => s.slice(0, -1)); setSelectedIndex(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [src, closeAndReset])

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current; if (!canvas) return
    const p = pointerToCanvas(e, canvas)
    // Select tool — pick the topmost mark under the pointer and start a
    // drag-to-move; clicking empty space clears the selection.
    if (tool === 'select') {
      const idx = findTopStrokeAt(strokes, p, 12, measureText)
      setSelectedIndex(idx >= 0 ? idx : null)
      if (idx >= 0) {
        canvas.setPointerCapture(e.pointerId)
        dragRef.current = { index: idx, last: p }
      }
      return
    }
    canvas.setPointerCapture(e.pointerId)
    if (tool === 'measure') {
      setDraftStroke({
        type: 'measure', start: p, end: p, label: '',
        color, width: DEFAULT_STROKE_WIDTH, fontSize: DEFAULT_FONT_SIZE,
      })
      return
    }
    if (tool === 'text') {
      try { canvas.releasePointerCapture(e.pointerId) } catch { /* no-op */ }
      void (async () => {
        const text = await promptDialog({
          title: 'Add label',
          inputLabel: 'Label text',
          placeholder: 'e.g. damaged here',
          required: true,
          submitLabel: 'Add label',
        })
        if (!text) return
        setStrokes((s) => [...s, {
          type: 'text', position: p, text, color, fontSize: DEFAULT_FONT_SIZE,
        }])
      })()
      return
    }
    if (tool === 'arrow' || tool === 'rect') {
      setDraftStroke({ type: tool, start: p, end: p, color, width: DEFAULT_STROKE_WIDTH })
      return
    }
    if (tool === 'freehand') {
      setDraftStroke({ type: 'freehand', points: [p], color, width: DEFAULT_STROKE_WIDTH })
    }
  }
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Select-drag: translate the picked stroke by the incremental delta.
    if (tool === 'select' && dragRef.current && canvasRef.current) {
      const p = pointerToCanvas(e, canvasRef.current)
      const { index, last } = dragRef.current
      const dx = p.x - last.x
      const dy = p.y - last.y
      if (dx || dy) {
        setStrokes((s) => s.map((st, i) => (i === index ? translateStroke(st, dx, dy) : st)))
        dragRef.current = { index, last: p }
      }
      return
    }
    if (!draftStroke || !canvasRef.current) return
    const p = pointerToCanvas(e, canvasRef.current)
    if (draftStroke.type === 'arrow' || draftStroke.type === 'rect' || draftStroke.type === 'measure') {
      setDraftStroke({ ...draftStroke, end: p })
    } else if (draftStroke.type === 'freehand') {
      setDraftStroke({ ...draftStroke, points: [...draftStroke.points, p] })
    }
  }
  const onPointerUp = () => {
    if (tool === 'select') { dragRef.current = null; return }
    if (!draftStroke) return
    // A measure commits with a typed label: drop the dimension line then
    // ask for the value. Cancelling still keeps the line (label empty);
    // the operator can double-click it later in Select mode to fill it in.
    if (draftStroke.type === 'measure') {
      const draft = draftStroke
      setDraftStroke(null)
      const len = Math.hypot(draft.end.x - draft.start.x, draft.end.y - draft.start.y)
      if (len < 5) return // ignore an accidental click with no drag
      void (async () => {
        const label = await promptDialog({
          title: 'Dimension',
          inputLabel: 'Measurement',
          placeholder: 'e.g. 2400mm',
          submitLabel: 'Add',
        })
        setStrokes((s) => [...s, { ...draft, label: label || '' }])
      })()
      return
    }
    setStrokes((s) => [...s, draftStroke])
    setDraftStroke(null)
  }

  // Double-click an existing label (in Select mode) to edit its text.
  const onDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool !== 'select') return
    const canvas = canvasRef.current; if (!canvas) return
    const p = pointerToCanvas(e, canvas)
    const idx = findTopStrokeAt(strokes, p, 12, measureText)
    if (idx < 0) return
    const current = getStrokeLabel(strokes[idx])
    if (current == null) return // not a label-bearing stroke
    setSelectedIndex(idx)
    void (async () => {
      const next = await promptDialog({
        title: 'Edit label',
        inputLabel: 'Text',
        defaultValue: current,
        required: true,
        submitLabel: 'Save',
      })
      if (next == null) return
      setStrokes((s) => s.map((st, i) => (i === idx ? setStrokeLabel(st, next) : st)))
    })()
  }

  async function handleCopy() {
    const canvas = canvasRef.current; if (!canvas) return
    repaint(strokes) // strip the dashed selection outline before export
    const blob = await exportCanvasToPng(canvas)
    paintLive()       // restore the on-screen selection outline
    if (!blob) { toast('Could not export markup', 'error'); return }
    const ok = await writeImageToClipboard(blob)
    toast(
      ok ? 'Marked-up image copied to clipboard' : 'Browser blocked clipboard write',
      ok ? 'success' : 'error',
    )
  }
  async function handleSend(intent: ComposeIntent) {
    const canvas = canvasRef.current; if (!canvas) return
    repaint(strokes) // strip the dashed selection outline before export
    const blob = await exportCanvasToPng(canvas)
    if (!blob) { toast('Could not export markup', 'error'); return }
    // Embed the marked-up image INLINE in the compose body (above the
    // reply quote) rather than as a detached attachment, so it actually
    // shows up in the email the recipient reads. ComposePage handles the
    // `inlineImageDataUrl` hand-off.
    const dataUrl = await blobToDataUrl(blob)
    setPending({ files: [], intent, inlineImageDataUrl: dataUrl })
    navigate(composeUrlFor(intent))
    closeAndReset()
  }

  const replyIntent = useMemo<ComposeIntent>(
    () => anchorEmailId
      ? { kind: 'reply', emailId: anchorEmailId }
      : { kind: 'new' },
    [anchorEmailId],
  )

  if (!src) return null

  // BUG-0030 (b): server-side render shape — when document is unavailable
  // (SSR / unit tests without jsdom) we fall back to in-tree rendering so
  // tests can still query the DOM with @testing-library. Branching here
  // keeps the production path clean (always portal) without forcing every
  // test environment to mock document.body.
  const drawerNode = (
    <div
      className="fixed inset-0 z-[100] flex justify-end bg-black/20 animate-in fade-in duration-200 motion-reduce:animate-none"
      role="dialog"
      aria-modal="true"
      aria-label="Image markup"
      data-testid="markup-drawer-root"
      onClick={closeAndReset}
    >
      {/* A side drawer, not a full modal overlay: light scrim (no
          blur) keeps the email readable behind it, and the panel
          slides in from the right so it reads as a drawer
          (operator request 2026-05-30). */}
      <div
        className="w-full sm:w-[80vw] md:w-[760px] lg:w-[900px] h-full bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Pen className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Mark up image</h2>
            <kbd className="ml-1 hidden sm:inline px-1.5 py-0.5 text-[10px] rounded bg-muted text-muted-foreground border border-border">⌘⇧M</kbd>
          </div>
          <button onClick={closeAndReset} className="p-1.5 rounded-md hover:bg-accent" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <MarkupToolbar
          tool={tool}
          color={color}
          onSelectTool={setTool}
          onSelectColor={setColor}
          onUndo={() => { setStrokes((s) => s.slice(0, -1)); setSelectedIndex(null) }}
          onClear={() => { setStrokes([]); setSelectedIndex(null) }}
        />

        <div className="relative flex-1 min-h-0 overflow-auto bg-muted/30 p-4 flex items-center justify-center">
          {/* BUG-0030 recurrence guard: the canvas must stay MOUNTED at
              all times - including while loading / in error - so
              `canvasRef.current` is non-null the instant the background
              `<img>` finishes decoding inside the load effect. The
              previous version conditionally unmounted the canvas behind
              `isLoading`, so `img.onload` ran against a null ref: the
              image was never sized onto the canvas and never painted,
              and the spinner cleared to reveal a blank 300×150 default
              rectangle. We now overlay the loading / error UI on top of
              a permanently-mounted (but hidden) canvas instead. */}
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={onDoubleClick}
            className={cn(
              'max-w-full max-h-full bg-white shadow-lg touch-none select-none',
              tool === 'select' ? 'cursor-move' : tool === 'text' ? 'cursor-text' : 'cursor-crosshair',
              (isLoading || loadError) && 'invisible',
            )}
            style={{ imageRendering: 'auto' }}
            data-testid="markup-canvas"
          />
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
              Loading image…
            </div>
          )}
          {!isLoading && loadError && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <MarkupLoadErrorPanel
                error={loadError}
                onRetry={() => setRetryNonce((n) => n + 1)}
              />
            </div>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 px-3 py-2 border-t border-border shrink-0">
          <button
            onClick={handleCopy}
            disabled={!!loadError || isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-accent border border-border disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
          <button
            onClick={() => handleSend({ kind: 'new' })}
            disabled={!!loadError || isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-accent border border-border disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-3.5 w-3.5" /> Send as new
          </button>
          {anchorEmailId && (
            <button
              onClick={() => handleSend(replyIntent)}
              disabled={!!loadError || isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-3.5 w-3.5" /> Send as reply
            </button>
          )}
        </footer>
      </div>
    </div>
  )

  // BUG-0030 (a) fix: portal the drawer to document.body so its
  // `position: fixed` is viewport-anchored regardless of what
  // transformed ancestors live above the mount point. See the file
  // header for the spec reference.
  if (typeof document === 'undefined' || !document.body) {
    return drawerNode
  }
  return createPortal(drawerNode, document.body)
}

/** BUG-0030: shape of a failure that prevents the drawer from
 *  showing the image. Distinguished so the error panel can give
 *  the operator the right next-action (retry vs. copy URL vs. ask
 *  ops to check CORS). */
export type MarkupLoadError =
  | { kind: 'fetch';  src: string }
  | { kind: 'decode'; src: string; mime: string }

function MarkupLoadErrorPanel({
  error, onRetry,
}: {
  error: MarkupLoadError
  onRetry: () => void
}) {
  const headline = error.kind === 'fetch'
    ? "Couldn't load that image"
    : "That image format isn't supported"
  const detail = error.kind === 'fetch'
    ? 'The browser refused to fetch the image bytes - usually CORS, an expired signed URL, or a missing session cookie.'
    : `The bytes loaded but the browser can't decode them${error.mime ? ` (${error.mime})` : ''}. Common causes: HEIC without codec, SVG with disallowed features, or a content-type mismatch.`
  return (
    <div
      role="alert"
      data-testid="markup-drawer-error"
      className="max-w-md text-center space-y-3 p-6 rounded-lg border border-destructive/30 bg-destructive/5"
    >
      <div className="flex items-center justify-center gap-2 text-destructive">
        <AlertCircle className="h-5 w-5" />
        <p className="text-sm font-semibold">{headline}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{detail}</p>
      <p className="text-[10px] font-mono text-muted-foreground/70 break-all px-2">
        {error.src}
      </p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:opacity-90"
      >
        <RotateCw className="h-3.5 w-3.5" /> Retry
      </button>
    </div>
  )
}

/** Dashed blue outline around the selected stroke. Drawn on screen only —
 *  callers repaint clean before exporting so it never bakes into the PNG. */
function drawSelectionBox(ctx: CanvasRenderingContext2D, b: Bounds): void {
  const pad = 6
  ctx.save()
  ctx.strokeStyle = '#2563eb'
  ctx.lineWidth = 2
  ctx.setLineDash([6, 4])
  ctx.strokeRect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2)
  ctx.restore()
}

